// chat ui should go here
// eventually want to handle editing as well
/* eslint-disable complexity */
import React, { useState, useEffect } from 'react';
import { ChatBulkAuditBatch, SubtaskAssignment } from '../SpeedAudit/types';
import { TaskProps } from 'react-scale/components/Task/TaskComponent';
import transformMultiTaskResponseToAssignment from '@scale/shared/assignmentJson/transformMultiTaskResponseToAssignment';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { normalizeAssignment } from '@scale/shared/assignmentJson/legacy/assignment-normalizer';
import { Typography, Icon, SelectMenu, ScaleLogoLoading, Modal, Button } from '@scale/scaleui';
import {
  useChatLayoutStore,
  ChatLayout,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatLayoutStore';
import InstructionsModalV2 from 'react-scale/dashboard.scale.com/components/Tasks/InstructionsModal/InstructionsModalV2';
import { SpeedAuditLookupResponse } from '@scale/shared/types/corp/attemptAudit';
import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
} from '@scale/shared/types/chat';
import { IProjectJson } from '@scale/shared/types/project';
import { ChatBulkAuditChatRenderer } from './ChatBulkAuditChatRenderer';
import useSupplementedMultiTaskResponseAssignment from 'react-scale/hooks/useSupplementedMultiTaskResponseAssignment';
import {
  useHistoricalResponseInAttemptAudit,
  useInternalAuditGradingInstructions,
} from 'react-scale/components/audits/ChatBulkAudit/apiClient';
import { useErrorBoundaryStore } from './hooks/useErrorBoundaryStore';
import {
  ChatBulkAuditAttemptList,
  getAttemptString,
  reviewStatusIcon,
  reviewStatusText,
  isSuccessStatus,
} from './ChatBulkAuditAttemptList';
import { isChatExperimentalTaskType } from './utils';
import { useUser } from 'react-scale/components/ui/UserContext';
import { ChatBulkAuditFormattedResponseViewer } from './PostFormatterCBA/ChatBulkAuditFormattedResponseViewer';
import { useShowContributorInstructionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useShowContributorInstructionsStore';
import { TaskType } from '@scale/shared-types';
import FrontierDataTaskPreview from 'react-scale/components/scalers/tasks/FrontierData/FrontierDataTaskPreview';
import { FrontierDataTaskParams } from '@scale/shared/types/frontierData';
import {
  MainSidebarMenu,
  QualityMenu,
  DataQualityTabs,
} from 'react-scale/corp/views/CorpProjectGroupManager/constants';

interface ChatBulkAuditMultiTurnResponseProps {
  auditBatch: ChatBulkAuditBatch;
  currentAttemptAudit: number;
  selectedAttempt: number;
  setSelectedAttempt: (index: number) => void;
  subtaskAssignment?: SubtaskAssignment;
  setSelectedAnnotation: (index: number) => void;
  showFeedbackBar: boolean;
  setShowFeedbackBar: (feedbackBarVisible: boolean) => void;
  allowEdits: boolean;
  formattedResponseView: boolean;
}

interface ChatBulkAuditResponseHeaderProps {
  attempts?: SpeedAuditLookupResponse[];
  setSelectedAttempt: (index: number) => void;
  selectedAttempt: number;
  project?: string;
  showFeedbackBar: boolean;
  setShowFeedbackBar: (showFeedbackBar: boolean) => void;
  taskType?: TaskType;
  allowEdits: boolean;
}

function ChatBulkAuditMultiTurnResponse(props: ChatBulkAuditMultiTurnResponseProps) {
  const { selectedAttempt, setSelectedAttempt } = props;
  const [renderedResponse, setRenderedResponse] = useState<JSX.Element | null>(null);

  const responses = props.subtaskAssignment?.responses;
  const subtask = props.subtaskAssignment?.subtask;
  const project =
    (subtask?.project as IProjectJson)?._id?.toString() ?? subtask?.project ?? undefined;

  const responseObj: SpeedAuditLookupResponse | undefined = responses?.[selectedAttempt];
  const { data, isLoading: isLoadingQueryOnlyValidWhenEnabled } =
    useHistoricalResponseInAttemptAudit(responseObj?.id, selectedAttempt !== 0);
  // Makes no sense but -> https://github.com/TanStack/query/issues/3584
  const isLoading = isLoadingQueryOnlyValidWhenEnabled && selectedAttempt !== 0;

  // TODO: Figure out if we need special formatting for feedback comment markdown & add here

  let taskProps: TaskProps | undefined;
  if (subtask) {
    taskProps = normalizeAssignment(subtask) as TaskProps;
    (taskProps.params as unknown as MultiTurnInteractionConfiguration).defaultExpand = false;
  }

  if (responseObj) {
    if (!responseObj.response) {
      responseObj.response = data;
    }
    //   taskProps.lintResults = responseObj.confidenceScores?.lintResults;
    //   taskProps.graderResponse = responseObj.graderResponse;
    //   taskProps.graderDiffResponse = responseObj.graderDiffResponse;
    //   taskProps.isAnswerKey = responseObj.isAnswerKey;
    //   taskProps.diffState = responseObj.diffState;
  }
  // if (responses.length > 0) {
  //   taskProps.taskerResponseInFeedback = responses[0].response;
  // }
  // taskProps.handleResponseSelect = this.handleResponseSelect;
  // taskProps.handleIdSelect = this.props.handleIdSelect;

  // For semseg audit
  // taskProps.isReviewMode = true;
  // taskProps.isPreviewMode = true;
  // taskProps.showHeader = false;
  // taskProps.multiTaskResponse = true;
  // taskProps.customDivHeight = '90vh';
  // taskProps.hideLabels = this.props.hideLabels;
  // taskProps.standalone = false;
  // taskProps.isRealCustomerAudit = this.props.isRealCustomerAudit;
  // taskProps.isSpeedAudit = true; // TODO: Don't need this!

  // TODO: fix these types

  let assignment: AssignmentJson | undefined;
  if (subtask) {
    assignment = transformMultiTaskResponseToAssignment(subtask, false, responseObj);
    assignment.reviewLevel = responseObj?.attemptedAtReviewLevel ?? assignment.reviewLevel;
  }
  const supplementedAssignment = useSupplementedMultiTaskResponseAssignment(assignment);
  const lastRenderedId = useErrorBoundaryStore(state => state.lastRenderedId);
  const resetBoundary = useErrorBoundaryStore(state => state.resetBoundary);
  const setResetBoundary = useErrorBoundaryStore(state => state.setResetBoundary);
  const user = useUser();

  // defer config updates
  useEffect(() => {
    if (!taskProps) {
      return;
    }
    if (props.subtaskAssignment?._id !== lastRenderedId && resetBoundary !== null) {
      resetBoundary();
      setResetBoundary(null);
    }
    const reviewedAttempt = props.subtaskAssignment?.auditedAttempt?.reviewedAttempt;

    setRenderedResponse(
      <div style={{ height: '90vh' }}>
        {(() => {
          if (isLoading && !responseObj?.response?.responses) {
            return (
              <div className="flex h-full w-full justify-center items-center">
                <ScaleLogoLoading size="lg" />
              </div>
            );
          }

          if (
            taskProps.type === TaskType.FrontierData &&
            props.subtaskAssignment?.auditedAttempt?.pipelineV3MongoDoc
          ) {
            return (
              <FrontierDataTaskPreview
                params={taskProps.params as unknown as FrontierDataTaskParams}
                previewParams={{
                  type: 'pipelinev3humannode',
                  nodeId: props.subtaskAssignment.auditedAttempt.pipelineV3MongoDoc,
                }}
              />
            );
          }

          if (props.formattedResponseView) {
            return (
              <ChatBulkAuditFormattedResponseViewer
                attemptAuditId={props.subtaskAssignment?._id}
                taskId={taskProps._id!}
                setShowFeedbackBar={props.setShowFeedbackBar}
              />
            );
          }

          if (taskProps.type === TaskType.Chat) {
            if (!responseObj?.response) {
              return null;
            }

            if (!responseObj?.response?.responses) {
              return (
                <Typography.Paragraph2 className="text-center">
                  Task response appears to be empty
                </Typography.Paragraph2>
              );
            }
          }

          if (isChatExperimentalTaskType(taskProps.type) && !responseObj?.response) {
            return null;
          }

          return (
            <ChatBulkAuditChatRenderer
              key={`${props.subtaskAssignment?.auditedAttempt?._id}-${props.subtaskAssignment?._id}`}
              _id={taskProps._id!}
              params={
                responseObj?.taskParamsFromV3Node ??
                (taskProps.params as unknown as MultiTurnInteractionConfiguration)
              }
              responses={
                responseObj?.response && isChatExperimentalTaskType(taskProps.type)
                  ? responseObj.response
                  : responseObj && responseObj?.response?.responses
                  ? (responseObj.response.responses as IndexedInteractionStepResponse[])
                  : []
              }
              auditAttemptId={props.subtaskAssignment?._id}
              project={project}
              taskType={taskProps.type}
              readOnly={!props.allowEdits}
              assignment={supplementedAssignment ?? assignment}
              reviewedTaskAttemptId={reviewedAttempt?.toString() ?? responseObj?.id}
              renderedTaskAttemptId={props.subtaskAssignment?.auditedAttempt?._id}
              loggedUserId={user.id}
              edited={props.subtaskAssignment?.edited}
              editedStepList={props.subtaskAssignment?.editedStepList}
            />
          );
        })()}
      </div>,
    );
  }, [
    project,
    props.subtaskAssignment,
    props.subtaskAssignment?._id,
    selectedAttempt,
    supplementedAssignment?.config,
    supplementedAssignment?.projectConfig,
    responseObj?.response?.responses,
    props.allowEdits,
    props.subtaskAssignment?.auditedAttempt?._id,
    user.id,
    props.formattedResponseView,
    props.setShowFeedbackBar,
  ]);
  // This seems like a little weird deps, but using supplementedAssignment results in a infinite loop.
  // TODO: We can probably ignore feature flag changes in supplementedMultiTaskResponse for now, but we should revisit this.

  return (
    <div className="bg-neutral-50 w-full" style={{ overflowY: 'auto' }}>
      <ChatBulkAuditResponseHeader
        attempts={responses}
        selectedAttempt={selectedAttempt}
        setSelectedAttempt={setSelectedAttempt}
        project={project}
        showFeedbackBar={props.showFeedbackBar}
        setShowFeedbackBar={props.setShowFeedbackBar}
        taskType={taskProps?.type}
        allowEdits={props.allowEdits}
      />
      {(!renderedResponse || !responseObj?.response) && (
        <div className="flex w-full h-full justify-center items-center">
          <ScaleLogoLoading size="lg" />
        </div>
      )}
      {/* How we get rendered response to load as quickly as possible */}
      <div className={responseObj?.response ? '' : 'hidden'}>{renderedResponse}</div>
    </div>
  );
}

const CUSTOMER_VIEW = 'customer-view';

function ChatBulkAuditLayoutConfigButton({ allowEdits }: { allowEdits: boolean }) {
  const { layout, setLayout } = useChatLayoutStore();
  const { customerAuditShowInstructions, setCustomerAuditShowInstructions } =
    useShowContributorInstructionsStore();

  useEffect(() => {
    setLayout(ChatLayout.Inline);
    setCustomerAuditShowInstructions(true);
  }, []);

  // When allowEdits becomes true, if in customer view, switch to inline view
  useEffect(() => {
    if (allowEdits && !customerAuditShowInstructions) {
      setCustomerAuditShowInstructions(true);
      setLayout(ChatLayout.Inline);
    }
  }, [allowEdits, customerAuditShowInstructions]);

  const handleLayoutSelection = (selectedOption: string) => {
    if (selectedOption === CUSTOMER_VIEW) {
      setCustomerAuditShowInstructions(false);
    } else {
      if (!customerAuditShowInstructions) {
        setCustomerAuditShowInstructions(true);
      }

      setLayout(selectedOption as ChatLayout);
    }
  };

  const selectedOption = !customerAuditShowInstructions
    ? CUSTOMER_VIEW
    : layout || ChatLayout.Inline;

  if (!layout) return null;

  if (allowEdits) {
    return null;
  }

  return (
    <SelectMenu
      selectedOptionId={selectedOption}
      setSelected={handleLayoutSelection}
      options={[
        { id: ChatLayout.Inline, name: 'Inline View' },
        { id: CUSTOMER_VIEW, name: 'Customer View' },
      ]}
      variant="minimal"
    />
  );
}

function ChatBulkAuditResponseHeader(props: ChatBulkAuditResponseHeaderProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showAttempts, setShowAttempts] = useState(false);
  const [showGradingInstructions, setShowGradingInstructions] = useState(false);
  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const layout = useChatLayoutStore(state => state.layout);

  const { data: gradingInstructions, isLoading: isLoadingGradingInstructions } =
    useInternalAuditGradingInstructions(props.project);

  const currentAttempt = props.attempts?.[0];
  // const tabs =
  //   props.attempts?.map((attempt, index) => ({
  //     id: index.toString(),
  //     icon:
  //       index === 0
  //         ? (['far', 'pencil'] as IconProp)
  //         : reviewStatusIcon[attempt.reviewStatus as string] ?? null,
  //     label: attempt.name,
  //   })) ?? [];
  const attempt = props.attempts?.[props.selectedAttempt];

  return (
    <div className="flex flex-row bg-neutral-0 px-8 justify-between gap-4">
      <div className="overflow-auto">
        <Button
          className="border-none"
          onClick={() => setShowAttempts(!showAttempts)}
          variant="transparent"
        >
          <div className="flex flex-row items-center justify-center gap-2">
            <Typography.Paragraph2>{attempt?.name}</Typography.Paragraph2>
            <Typography.Paragraph3 className="text-neutral-400">
              {props.selectedAttempt === 0
                ? 'Auditing (latest)'
                : getAttemptString((props.attempts?.length ?? 0) - props.selectedAttempt)}
            </Typography.Paragraph3>
            <div className="bg-neutral-100 flex flex-row gap-0 items-center justify-center pr-2 rounded-md">
              <Icon
                icon={reviewStatusIcon[attempt?.reviewStatus ?? ''] ?? ['far', 'pen-circle']}
                size="sm"
                variant={isSuccessStatus(attempt?.reviewStatus) ? 'success' : 'neutral'}
                rounded={true}
              />
              <Typography.Paragraph3
                className={
                  'font-semibold ' +
                  (isSuccessStatus(attempt?.reviewStatus) ? 'text-success-800' : '')
                }
              >
                {reviewStatusText[attempt?.reviewStatus ?? ''] ?? 'Pending'}
              </Typography.Paragraph3>
            </div>
            <Icon icon={['far', showAttempts ? 'chevron-up' : 'chevron-down']} variant="neutral" />
          </div>
        </Button>
        {showAttempts && (
          <ChatBulkAuditAttemptList
            attempts={props.attempts}
            setSelectedAttempt={(idx: number) => {
              setShowAttempts(false);
              props.setSelectedAttempt(idx);
            }}
            selectedAttempt={props.selectedAttempt}
            setOpen={(open: boolean) => setShowAttempts(open)}
          />
        )}
        {/* <Tabs
          className=""
          onChange={index => props.setSelectedAttempt(Number(index))}
          tabs={tabs}
          value={props.selectedAttempt.toString()}
          variant="default"
        /> */}
      </div>
      <div className="flex flex-row gap-1 items-center">
        {props.taskType === TaskType.Chat && (
          <ChatBulkAuditLayoutConfigButton allowEdits={props.allowEdits} />
        )}
        {layout === ChatLayout.Inline && (
          <Icon
            icon="thumbtack"
            onClick={() => setIsDrawerOpen(!isDrawerOpen)}
            tooltip="Pin steps"
          />
        )}
        <Icon
          icon="file-alt"
          onClick={() => setShowGradingInstructions(!showGradingInstructions)}
          tooltip="View grading instructions"
        />
        <Icon
          icon="books"
          tooltip="View task instructions"
          onClick={() => setShowInstructions(!showInstructions)}
        />
        <Icon
          icon="sidebar-flip"
          tooltip={props.showFeedbackBar ? 'Hide Feedback Bar' : 'Show Feedback Bar'}
          onClick={() => props.setShowFeedbackBar(!props.showFeedbackBar)}
        />
      </div>
      <InstructionsModalV2
        isModalOpen={showInstructions}
        hideModal={() => setShowInstructions(false)}
        rawJSON={JSON.stringify(currentAttempt)}
        zIndex={100}
        project={props.project}
        assignmentId={currentAttempt?.id}
        userIsCustomer={true}
      />
      <Modal
        open={showGradingInstructions}
        title="Grading Instructions"
        onClose={() => setShowGradingInstructions(false)}
        innerClassName={gradingInstructions ? 'w-4/5 h-full' : ''}
      >
        {isLoadingGradingInstructions ? (
          <div className="flex flex-col content-center items-center p-4">
            <ScaleLogoLoading size="lg" />
          </div>
        ) : gradingInstructions?.internalUrl ? (
          <iframe src={gradingInstructions.internalUrl} frameBorder="0" className="w-full h-full" />
        ) : (
          <div className="flex flex-col content-center items-center p-4">
            <Icon icon={['far', 'file']} size="lg" rounded hasBackground />
            <p className="mt-2 font-semibold">No grading instructions found</p>
            <p className="mt-2 text-sm text-center">
              No audit instructions or rubrics have been provided for this project.
              <br />
              Go to the{' '}
              {props.project ? (
                <a
                  href={`https://dashboard.scale.com/corp/genai-ops-hub/projects/${props.project}/${MainSidebarMenu.Quality}/${QualityMenu.DataQuality}/${DataQualityTabs.QCMetrics}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-RemoBlue)' }}
                >
                  QC Center
                </a>
              ) : (
                'QC Center'
              )}{' '}
              in the GenAI Ops Hub to set this up.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default React.memo(ChatBulkAuditMultiTurnResponse);
